/**
 * AppointmentsLanding — the FSR-facing Appointments tab.
 *
 * This is the unified shell that fully leverages M365 Calendar +
 * MS Bookings + Teams Meetings (Bill 2026-04-23 brief). One landing,
 * five tabs, no duplication, no stub sub-flows.
 *
 *   Today    (default for FSR / Officer)    — /me/calendarView (today)
 *   Upcoming                                — /me/calendarView (next 7d)
 *   Bookings                                — /solutions/bookingBusinesses
 *   Manage   (Supervisor+)                  — branch-wide aggregate + reassign
 *   Config   (Manager+)                     — Bookings business id per branch
 *
 * The 5-step customer-facing wizard (BookingWizard) is the public booking
 * flow — kept intact, reachable via /rae/book and the BookOnBehalf wrapper.
 */

import { useMemo, useState } from 'react';
import { Pill, Button } from '@/components/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { TodayTab } from './tabs/TodayTab';
import { UpcomingTab } from './tabs/UpcomingTab';
import { BookingsTab } from './tabs/BookingsTab';
import { ManageTab } from './tabs/ManageTab';
import { ConfigTab } from './tabs/ConfigTab';
import { NewAppointmentModal } from './components/NewAppointmentModal';
import './styles/appointments.css';

type TabKey = 'today' | 'upcoming' | 'bookings' | 'manage' | 'config';

interface TabSpec {
  key: TabKey;
  label: string;
  /** Roles that may see this tab. */
  visibleToRoles: ReadonlyArray<string>;
}

const TABS: ReadonlyArray<TabSpec> = [
  { key: 'today', label: 'Today', visibleToRoles: ['Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'] },
  { key: 'upcoming', label: 'Upcoming', visibleToRoles: ['Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'] },
  { key: 'bookings', label: 'Bookings', visibleToRoles: ['Officer', 'Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'] },
  { key: 'manage', label: 'Manage', visibleToRoles: ['Supervisor', 'Manager', 'Executive', 'Admin', 'SuperAdmin'] },
  { key: 'config', label: 'Config', visibleToRoles: ['Manager', 'Executive', 'Admin', 'SuperAdmin'] },
];

export function AppointmentsLanding(): JSX.Element {
  const { user } = useAuth();
  const [active, setActive] = useState<TabKey>('today');
  const [newApptOpen, setNewApptOpen] = useState<boolean>(false);

  const role = user?.primaryRole ?? 'Officer';
  const visibleTabs = useMemo(
    () => TABS.filter((t) => t.visibleToRoles.includes(role)),
    [role],
  );

  return (
    <div className="module-page appt-landing">
      <header className="module-page__header">
        <div className="appt-landing__title-row">
          <h1>Appointments</h1>
          <Pill variant="muted">Module RAE</Pill>
          <Pill variant="info">M365 Calendar · Bookings · Teams</Pill>
        </div>
        <p className="module-landing__scope">
          One coherent surface for everything appointment — your daily calendar,
          customer-self-booked Bookings appointments, and Teams meetings — with a
          unified create flow that picks the right channel for each customer.
        </p>
      </header>

      <nav className="appt-landing__tabs" role="tablist" aria-label="Appointments sections">
        {visibleTabs.map((t) => {
          const isActive = active === t.key;
          return (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={`appt-landing__tab ${isActive ? 'is-active' : ''}`.trim()}
              onClick={() => setActive(t.key)}
            >
              {t.label}
            </button>
          );
        })}
        <div className="appt-landing__tabs-spacer" />
        <Button variant="primary" size="sm" onClick={() => setNewApptOpen(true)}>
          + New appointment
        </Button>
      </nav>

      <div className="appt-landing__body" role="tabpanel">
        {active === 'today' && <TodayTab onNewAppointment={() => setNewApptOpen(true)} />}
        {active === 'upcoming' && <UpcomingTab />}
        {active === 'bookings' && <BookingsTab />}
        {active === 'manage' && <ManageTab />}
        {active === 'config' && <ConfigTab />}
      </div>

      <NewAppointmentModal
        open={newApptOpen}
        onClose={() => setNewApptOpen(false)}
        onCreated={() => {
          setNewApptOpen(false);
          // Light-touch refresh — switch back to Today to surface the new card.
          setActive('today');
        }}
      />
    </div>
  );
}
